import { useEffect } from "react";

const DEFAULT_IMAGE = "/images/logo/BPF-logo-1200x630.png";
const DEFAULT_TITLE = "Bikeparks Finder — Bike parks & MTB trails";
const DEFAULT_DESCRIPTION = "Discover verified bike parks, trail centers and MTB trails. Compare riding styles, trail difficulty, access and practical visit information.";
const SITE_NAME = "Bikeparks Finder";

const siteOrigin = () => {
  const configured = (process.env.REACT_APP_SITE_URL || "").trim();
  return (configured || window.location.origin).replace(/\/$/, "");
};

const absoluteUrl = (value) => {
  if (!value) return undefined;
  try {
    return new URL(value, `${siteOrigin()}/`).toString();
  } catch {
    return undefined;
  }
};

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
};

const upsertLink = (rel, href) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

const json = (value) => JSON.stringify(value).replace(/</g, "\\u003c");

export function Seo({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  noIndex = false,
  structuredData,
}) {
  useEffect(() => {
    const canonical = absoluteUrl(path) || siteOrigin();
    const imageUrl = absoluteUrl(image);

    document.title = title;
    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    if (imageUrl) {
      upsertMeta('meta[property="og:image"]', { property: "og:image", content: imageUrl });
      upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: imageUrl });
    }
    upsertLink("canonical", canonical);

    const id = "bikeparks-finder-structured-data";
    document.getElementById(id)?.remove();
    if (structuredData) {
      const script = document.createElement("script");
      script.id = id;
      script.type = "application/ld+json";
      script.text = json(structuredData);
      document.head.appendChild(script);
    }

    return () => document.getElementById(id)?.remove();
  }, [title, description, path, image, type, noIndex, structuredData]);

  return null;
}

const noIndexPath = (pathname) => /^\/(?:admin|operator|favorites|my-map|profile|login|register|verify-email|onboarding|ride-plan|install|privacy|terms|impressum)(?:\/|$)/.test(pathname);
const deploymentIsNoIndex = () => (process.env.REACT_APP_SEO_INDEXABLE || "").trim().toLowerCase() === "false";

export function RouteSeo({ pathname }) {
  const isEvents = pathname === "/events";
  const title = isEvents
    ? "Mountain bike events and rides — Bikeparks Finder"
    : DEFAULT_TITLE;
  const description = isEvents
    ? "Find upcoming mountain bike events and group rides near bike parks and trail centers."
    : DEFAULT_DESCRIPTION;

  return (
    <Seo
      title={title}
      description={description}
      path={pathname}
      noIndex={deploymentIsNoIndex() || noIndexPath(pathname)}
      structuredData={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: siteOrigin(),
        description: DEFAULT_DESCRIPTION,
        inLanguage: "en",
      }}
    />
  );
}

export const buildParkSchema = (park, path, image) => {
  const address = [park.city, park.country].filter(Boolean).join(", ");
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SportsActivityLocation",
        "@id": absoluteUrl(path),
        name: park.name,
        description: park.description || park.summary,
        url: absoluteUrl(path),
        image: absoluteUrl(image),
        sport: "Mountain biking",
        geo: { "@type": "GeoCoordinates", latitude: park.lat, longitude: park.lng },
        address: address ? { "@type": "PostalAddress", addressLocality: park.city || undefined, addressCountry: park.country || undefined } : undefined,
        sameAs: park.website || undefined,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: SITE_NAME, item: siteOrigin() },
          { "@type": "ListItem", position: 2, name: park.name, item: absoluteUrl(path) },
        ],
      },
    ],
  };
  if (park.rating_count > 0 && park.rating > 0) {
    schema["@graph"][0].aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(park.rating).toFixed(1),
      reviewCount: park.rating_count,
      bestRating: "5",
      worstRating: "1",
    };
  }
  return schema;
};

export const buildTrailSchema = (trail, path, image) => {
  const parkPath = `/park/${trail.park?.url_key || trail.park?.id}`;
  const properties = [
    ["Difficulty", trail.difficulty],
    ["Length", trail.length_km != null ? `${trail.length_km} km` : null],
    ["Elevation gain", trail.elevation_gain_m != null ? `${Math.round(trail.elevation_gain_m)} m` : null],
    ["Elevation loss", trail.elevation_loss_m != null ? `${Math.round(trail.elevation_loss_m)} m` : null],
    ["Trail type", trail.trail_type],
    ["Riding style", trail.riding_style],
    ["Surface", trail.surface],
    ["Operational status", trail.status],
  ].filter(([, value]) => value);

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TouristAttraction",
        "@id": absoluteUrl(path),
        name: trail.name,
        description: trail.description || `${trail.name} mountain bike trail at ${trail.park?.name || "Bikeparks Finder"}.`,
        url: absoluteUrl(path),
        image: absoluteUrl(image),
        touristType: "Mountain biking",
        isPartOf: {
          "@type": "SportsActivityLocation",
          name: trail.park?.name,
          url: absoluteUrl(parkPath),
        },
        additionalProperty: properties.map(([name, value]) => ({ "@type": "PropertyValue", name, value })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: SITE_NAME, item: siteOrigin() },
          { "@type": "ListItem", position: 2, name: trail.park?.name, item: absoluteUrl(parkPath) },
          { "@type": "ListItem", position: 3, name: trail.name, item: absoluteUrl(path) },
        ],
      },
    ],
  };
};
